import React, { useEffect, useState } from "react";
import { Stack } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import ListButton from "components/common/ListButton";
import CustomModal from "components/common/CustomModal";
import { useSubscriptionPause } from "hooks/useSubscriptionPause";
import {
  formatPausePeriodText,
  getExhaustMessage,
  getPauseButtonState,
  PAUSE_DEFAULT_GUIDE,
} from "utils/subscriptionPauseUtils";
import useUnifiedNavigate from "../../hooks/useUnifiedNavigate";

// 라벨(좌) + 값(우=사용/총) 1행
function PauseStatRow({
  label,
  used,
  total,
  unit,
}: {
  label: string;
  used: number;
  total: number;
  unit: string;
}) {
  return (
    <Stack
      direction={"row"}
      width={"100%"}
      height={"48px"}
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: "12px" }}
    >
      <CustomTypography
        variant={"Body"}
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
      >
        {label}
      </CustomTypography>
      <CustomTypography variant={"Body"} sx={{ fontWeight: 700 }}>
        <span style={{ color: theme.vars.palette.Content.Strong }}>
          {`${used}${unit}`}
        </span>
        <span style={{ color: theme.vars.palette.Content.Weak }}>
          {` / ${total}${unit}`}
        </span>
      </CustomTypography>
    </Stack>
  );
}

export default function PauseStatusSection() {
  const { data: pause, refetch } = useSubscriptionPause();
  const unifiedNavigate = useUnifiedNavigate();
  const [pausingModalOpen, setPausingModalOpen] = useState(false);

  // 예약/변경/취소는 별도 카드 webview(/subscription-pause)에서 일어나고
  // 거기서 한 invalidate 는 이 webview 캐시에 닿지 않는다. 카드가 닫혀
  // 이 화면이 다시 foreground 로 돌아올 때 직접 리페치해 현황을 동기화.
  // (SecuritySettingsTask 의 visibilitychange/focus 복귀 갱신 패턴과 동일)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [refetch]);

  if (!pause?.isAvailable) return null;

  const buttonState = getPauseButtonState(pause);
  const exhaustMessage = getExhaustMessage(pause);
  const reservation = pause.reservation;

  const buttonLabel =
    buttonState === "active"
      ? "일시정지 중"
      : buttonState === "reserved"
        ? "일시정지 예약됨"
        : "일시정지하기";

  const handleButtonClick = () => {
    if (buttonState === "active") {
      setPausingModalOpen(true);
      return;
    }
    // reservable → 예약, reserved → 변경 (페이지 내부에서 모드 분기)
    unifiedNavigate(`/subscription-pause`, {
      type: "card",
      animation: "default",
      gestureEnabled: true,
    });
  };

  return (
    <>
      <Stack spacing={"12px"} mt={"32px"}>
        <Stack
          justifyContent="space-between"
          sx={{
            borderRadius: "16px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            p: 1,
          }}
        >
          <PauseStatRow
            label={"일시정지 기간"}
            used={pause.usedDays}
            total={pause.totalDays}
            unit={"일"}
          />
          <PauseStatRow
            label={"일시정지 횟수"}
            used={pause.usedCount}
            total={pause.totalCount}
            unit={"회"}
          />
        </Stack>

        <Stack spacing={1} px={1}>
          <CustomTypography
            variant={"Caption"}
            sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
          >
            {PAUSE_DEFAULT_GUIDE}
          </CustomTypography>
          {exhaustMessage && (
            <CustomTypography
              variant={"Caption"}
              sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
            >
              {exhaustMessage}
            </CustomTypography>
          )}
        </Stack>
      </Stack>

      {buttonState !== "hidden" && (
        <Stack spacing={"12px"} mt={"32px"}>
          <ListButton
            variant={"Plain"}
            color={"neutral"}
            text={
              <span style={{ color: theme.vars.palette.Content.Highlight }}>
                {buttonLabel}
              </span>
            }
            onClick={handleButtonClick}
          />
        </Stack>
      )}

      {reservation && (
        <CustomModal
          open={pausingModalOpen}
          onClose={() => setPausingModalOpen(false)}
          title={"현재 일시정지 중입니다"}
          subtitle={`${formatPausePeriodText(
            dayjs(reservation.startDate),
            dayjs(reservation.endDate)
          )} 프로그램 이용을 잠시 중단한 상태입니다.`}
          actions={[
            {
              label: "확인",
              onClick: () => setPausingModalOpen(false),
              variant: "Solid",
            },
          ]}
        />
      )}
    </>
  );
}
